import {Injectable} from '@angular/core';
import {Route} from '../models/route.model';
import {Stop} from '../models/stop.model';
import {Subject} from 'rxjs';
import {NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {RouteGroup} from '../models/routeGroup.model';
import {DispatchGroup} from '../models/dispatchGroup.model';
import {BlockedDate} from '../models/blockedDate.model';
import {Vehicle} from '../models/vehicle.model';

@Injectable({providedIn: 'root'})
export class RouteService {

    private dispatchGroups: DispatchGroup[] = [];
    private routes: Route[] = [];
    private routeGroups: RouteGroup[] = [];
    private stops: Stop[] = [];
    private stopTitles: any[] = [];
    private blockedDates: BlockedDate[] = [];

    private vehicles: Vehicle[] = [];

    dispatchGroupsChanged = new Subject<DispatchGroup[]>();

    stopsChanged = new Subject<Stop[]>();
    routesChanged = new Subject<Route[]>();
    routeGroupsChanged = new Subject<RouteGroup[]>();
    blockedDatesChanged = new Subject<BlockedDate[]>();
    vehiclesChanged = new Subject<Vehicle[]>();

    constructor(private ngDateParserFormatter: NgbDateParserFormatter) {
    }

    setDispatchGroup(dispatchGroups: DispatchGroup[]) {
        this.dispatchGroups = dispatchGroups;
        this.dispatchGroupsChanged.next(this.dispatchGroups.slice());
    }

    getDispatchGroup() {
        return this.dispatchGroups;
    }

    setRoute(routes: Route[]) {
        this.routes = routes;
        this.routesChanged.next(this.routes.slice());
    }

    getRoute() {
        return this.routes;
    }

    setRouteGroup(routeGroups: RouteGroup[]) {
        this.routeGroups = routeGroups;
        this.routeGroupsChanged.next(this.routeGroups.slice());
    }

    getRouteGroup() {
        return this.routeGroups;
    }

    getRouteGroupById(id: number) {
        return this.routeGroups.filter(x => x.id === id)[0];
    }

    getRouteGroupTitleById(id: number) {
        return this.routeGroups.filter((el) => +el.id === +id)[0].title;
    }

    getRouteByGroupId(routeGroup_id: number) {
        // retrieve routes in order by start date
        return this.routes.filter(x => x.route_group_id === routeGroup_id).sort(
            (a, b) => {
                if (a.start_date < b.start_date) {
                    return 1;
                } else if (a.start_date > b.start_date) {
                    return -1;
                } else {
                    return 0;
                }
            });
    }

    getRouteTimeById(route_id: number) {
        const route = this.routes.filter(x => x.id === route_id)[0];
        return route.pick_time + ' - ' + route.drop_time;
    }

    getDispatchGroupIdByRouteId(route_id: number) {
        const route_group_id = this.getRouteGroupIdByRouteId(route_id);
        return this.routeGroups.filter(x => x.id === route_group_id)[0].dispatch_group_id;
    }

    getRouteGroupIdByRouteId(route_id: number) {
        return this.routes.filter(x => x.id === route_id)[0].route_group_id;
    }

    setStop(stops: Stop[]) {
        this.stops = stops;
        this.stopsChanged.next(this.stops.slice());
    }

    getStop() {
        return this.stops;
    }

    getStopTitleOne(id: number) {
        return this.stops.find((el) => el.id === id).title;
    }

    getAvailableDepStopFrom() {
        let availableDepStopFrom = [];
        for (const route of this.routes) {
            availableDepStopFrom.push(route.pick_stop);
        }
        availableDepStopFrom = availableDepStopFrom.reduce((unique, o) => {
            if (!unique.some(obj => obj.id === o.id && obj.title === o.title)) {
                unique.push(o);
            }
            return unique;
        }, []);
        return availableDepStopFrom;
    }

    getAvailableReStopFrom(depPickStop: number) {
        let availableReStopFrom = [];
        const dep_route_group_ids = this.getRouteGroupByPickStopId(depPickStop);
        for (const route of this.routes) {
            // remove same route group id
            // remove same departure city
            if (route.route_group_id in dep_route_group_ids || route.pick_stop_id === depPickStop) {
            } else {
                availableReStopFrom.push(route.pick_stop);
            }
        }
        availableReStopFrom = availableReStopFrom.reduce((unique, o) => {
            if (!unique.some(obj => obj.id === o.id && obj.title === o.title)) {
                unique.push(o);
            }
            return unique;
        }, []);
        return availableReStopFrom;
    }

    setAvailableDepStopTo(stopFrom: number) {
        // init
        let availableDepStopTo = [];
        for (const route of this.routes) {
            if (route.pick_stop_id === stopFrom) {
                availableDepStopTo.push(route.drop_stop_id);
            }
        }
        // remove duplicates
        availableDepStopTo = availableDepStopTo.filter((el, i, a) => i === a.indexOf(el));
        availableDepStopTo = this.getStopTitle(availableDepStopTo);
        return availableDepStopTo;
    }

    setAvailableReStopTo(stopFrom: number) {
        // init
        let availableReStopTo = [];
        for (const route of this.routes) {
            if (route.pick_stop_id === stopFrom) {
                availableReStopTo.push(route.drop_stop_id);
            }
        }
        // remove duplicates
        availableReStopTo = availableReStopTo.filter((el, i, a) => i === a.indexOf(el));
        availableReStopTo = this.getStopTitle(availableReStopTo);
        return availableReStopTo;
    }

    setSelectedDepRoute(stopFrom: number, stopTo: number, dateRaw: any) {
        const date = typeof (dateRaw) === 'string' ? dateRaw : this.ngDateParserFormatter.format(dateRaw);
        let selectedDepRoute = []; // Route[]
        for (const route of this.routes) {
            if (route.pick_stop_id === stopFrom && route.drop_stop_id === stopTo
                && (date >= route.start_date && date <= route.end_date)) {
                selectedDepRoute.push(route);
            }
        }
        return selectedDepRoute;
    }

    setSelectedReRoute(stopFrom: number, stopTo: number, dateRaw: any) {
        const date = typeof (dateRaw) === 'string' ? dateRaw : this.ngDateParserFormatter.format(dateRaw);
        let selectedReRoute = [];
        for (const route of this.routes) {
            if (route.pick_stop_id === stopFrom && route.drop_stop_id === stopTo
                && (date >= route.start_date && date <= route.end_date)) {
                selectedReRoute.push(route);
            }
        }
        return selectedReRoute;
    }

    getRouteGroupByPickStopId(id: number) {
        let available_dep_route_group_ids = [];
        for (const route of this.routes) {
            if (route.pick_stop_id === id) {
                available_dep_route_group_ids.push(route.route_group_id);
            }
        }
        available_dep_route_group_ids = available_dep_route_group_ids.filter((el, i, a) => i === a.indexOf(el));
        return available_dep_route_group_ids;
    }

    getStopTitle(ids: number[]) {
        this.stopTitles = [];
        for (const stop of this.stops) {
            for (const id of ids) {
                if (stop.id === id) {
                    const element = {id: id, title: stop.title};
                    this.stopTitles.push(element);
                }
            }
        }
        return this.stopTitles;
    }

    getStopById(id: number) {
        for (const stop of this.stops) {
            if (stop.id === id) {
                return stop;
            }
        }
    }

    setBlockedDates(blockedDates: BlockedDate[]) {
        if (blockedDates.length > 0) {
            this.blockedDates = blockedDates;
            this.blockedDatesChanged.next(blockedDates.slice());
        }
    }

    getBlockedDatesByMonth(month: string) {
        let selected_blocked_date: any[] = [];
        for (const blockedDate of this.blockedDates) {
            if (blockedDate.date.substring(0, 7) === month) {
                selected_blocked_date.push({date: blockedDate.date, dispatch_group_id: blockedDate.dispatch_group_id});
            }
        }
        return selected_blocked_date;
    }

    getBlockedDates() {
        let blocked_date_array: any[] = [];
        for (const blockedDate of this.blockedDates) {
            blocked_date_array.push({date: blockedDate.date, dispatch_group_id: blockedDate.dispatch_group_id});
        }
        return blocked_date_array;
    }

    setVehicles(vehicles: Vehicle[]) {
        this.vehicles = vehicles;
        this.vehiclesChanged.next(this.vehicles.slice());
    }

    getVehicles() {
        return this.vehicles;
    }

    getPriceByRouteIdAndPassType(id: number, type: string) {
        if (type === 'Adult') {
            return this.routes.find((el) => el.id === id).adult_price;
        } else if (type === 'Child') {
            return this.routes.find((el) => el.id === id).child_price;
        } else {
            return this.routes.find((el) => el.id === id).infant_price;
        }
    }
    /**
     * Look up a Route by id from the in-memory list.
     * Returns null when the route is not loaded yet or doesn't exist,
     * so callers can render "Not available" instead of crashing.
     */
    getRouteById(id: number): Route | null {
        if (id === null || id === undefined) {
            return null;
        }
        const found = this.routes.find((el) => +el.id === +id);
        return found ? found : null;
    }

}
